import { orderBy } from 'lodash';
import moment from 'moment';
import { createSelector } from 'reselect';
import {
  ConnectionModel,
  FormattedConnectionModel,
} from '../../model/ConnectionModel';
import { StoreState } from '../../store-interfaces';

const getConnections = (state: StoreState) => state.connections.entities;
const getConnectionsSortBy = (state: StoreState) =>
  state.connectionsTableState.connectionsOrderBy;
const getActiveIntegration = (state: StoreState) =>
  state.connectionsTableState.activeIntegration;

const getOrderedConnections = (
  connections: ConnectionModel[],
  connectionsOrderBy,
): FormattedConnectionModel[] => {
  const { orderDir, keyName } = connectionsOrderBy;
  const mappedConnections = connections.map<FormattedConnectionModel>(
    connection => {
      const { createdAt, updatedAt, active } = connection;
      const formattedCreatedAt = moment(createdAt).format('MM/DD/YYYY');
      const formattedUpdatedAt = moment(updatedAt).format('MM/DD/YYYY');
      const statusText = active ? 'Active' : 'Draft';

      return {
        ...connection,
        statusText,
        formattedUpdatedAt,
        formattedCreatedAt,
      };
    },
  );
  return orderBy(
    mappedConnections,
    [connection => connection[keyName]?.toLowerCase()],
    [orderDir],
  );
};

export const orderedConnectionsSelector = createSelector(
  [getConnections, getConnectionsSortBy],
  getOrderedConnections,
);

export const activeIntegrationSelector = getActiveIntegration;

export type GoogleSheetsConnections = {
  accountId: number | null;
  connections: FormattedConnectionModel[];
  accountEmail: string | null;
};

export const selectConnectionsGoogleAccounts = createSelector(
  [getConnections],
  connections => {
    const accounts = connections.reduce(
      (accounts: { id: number; email: string }[], connection) => {
        if (
          accounts.find(account => account.id === connection.googleAccountId)
        ) {
          return accounts;
        }
        return [
          ...accounts,
          {
            id: connection.googleAccountId,
            email: connection.googleAccountEmail,
          },
        ];
      },
      [],
    );
    return accounts;
  },
);

export const selectJSONWebhooksConnections = createSelector(
  [orderedConnectionsSelector],
  connections =>
    connections.filter(
      connection => connection.integrationType === 'default',
    ) ?? [],
);

export const selectKlaviyoConnections = createSelector(
  [getConnections, getConnectionsSortBy],
  connections => connections.filter(c => c.integrationType === 'klaviyo'),
);

export const selectSalesForceConnections = createSelector(
  [getConnections, getConnectionsSortBy],
  connections => connections.filter(c => c.integrationType === 'salesforce'),
);

export const selectGoHighLevelConnections = createSelector(
  [getConnections, getConnectionsSortBy],
  connections => connections.filter(c => c.integrationType === 'gohighlevel'),
);

export const selectSendlaneConnections = createSelector(
  [getConnections, getConnectionsSortBy],
  connections => connections.filter(c => c.integrationType === 'sendlane'),
);

export const selectSlackConnections = createSelector(
  [getConnections, getConnectionsSortBy],
  connections => connections.filter(c => c.integrationType === 'slack'),
);

export const selectShopifyConnections = createSelector(
  [getConnections, getConnectionsSortBy],
  connections => connections.filter(c => c.integrationType === 'shopify'),
);

export const selectGoogleSheetsConnectionsByAccountId = createSelector(
  [orderedConnectionsSelector, selectConnectionsGoogleAccounts],
  (connections, accounts): GoogleSheetsConnections[] =>
    accounts.map(account => ({
      accountId: account.id,
      accountEmail: account.email,
      connections:
        connections.filter(
          connection =>
            connection.googleAccountId === account.id &&
            connection.integrationType === 'google_spreadsheet',
        ) ?? [],
    })),
);

export const selectFacebookAdsConnections = createSelector(
  [orderedConnectionsSelector],
  connections =>
    connections.filter(
      connection => connection.integrationType === 'facebook_ads_audience',
    ) ?? [],
);
